import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getLoanAmendments,
  addLoanAmendment,
  editLoanAmendment,
  getLoanAmendment,
  submitLoanAmendment,
  rejectLoanAmendment,
  approveLoanAmendment,
  deleteLoanAmendment,
  getFacilityLoanAmendments,
  startLoadingFacilityLoanAmendments,
  stopLoadingFacilityLoanAmendments,
} from './actions'
import { DraftLoanParams, LoanParams } from 'app/models/loan-params'
import { getBusinessDateFromState } from '../business-date/thunks'
import { formatDateToUTC } from 'app/utils/util-functions'
import { loadAllLoans, loadLockedLoans } from '../loans/thunks'
import { coalesceThunkByKey } from 'app/state/utils/coalesce-thunk'

export const saveLoanAmendment =
  (savedLoan: DraftLoanParams, loan?: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    const businessDate = getBusinessDateFromState()

    // Determine amendment type and date based on business logic
    const amendmentType = savedLoan.amendmentType ?? 'LoanAmendment'
    const amendmentDate =
      amendmentType === 'LoanEdit'
        ? undefined // Loan edits don't have effective dates (retroactive to start date)
        : (savedLoan.amendmentDate ?? formatDateToUTC(businessDate)) // Loan amendments need future effective date

    const updated_savedLoan: DraftLoanParams = {
      ...savedLoan,
      amendmentType,
      amendmentDate,
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    }

    if (loan) {
      // Edit existing draft amendment
      return client
        .put(`${apiServerUrl}/api/amendment-draft-loans`, updated_savedLoan)
        .then(response => {
          dispatch(editLoanAmendment(response.data))
          return { success: true, payload: response.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    } else {
      // Create new amendment from approved loan
      return client
        .post(`${apiServerUrl}/api/approved-loans/amendment`, updated_savedLoan)
        .then(response => {
          dispatch(addLoanAmendment(response.data))
          return { success: true, payload: response.data }
        })
        .catch(e => {
          return {
            success: false,
            payload: e.response?.data?.error || e.message,
          }
        })
    }
  }

const loadDraftLoanAmendments = async (facilityId?: string) => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(
      `${apiServerUrl}/api/amendment-draft-loans${
        facilityId ? `/facility?id=${facilityId}` : ''
      }`,
    )
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

const loadSubmittedLoanAmendments = async (facilityId?: string) => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(
      `${apiServerUrl}/api/amendment-submitted-loans${
        facilityId ? `/facility?id=${facilityId}` : ''
      }`,
    )
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadAllLoanAmendments = coalesceThunkByKey(
  (facilityId?: string) => async (dispatch: Dispatch) => {
    if (facilityId) {
      dispatch(startLoadingFacilityLoanAmendments())
    }

    try {
      const [draftLoans, submittedLoans] = await Promise.all([
        loadDraftLoanAmendments(facilityId),
        loadSubmittedLoanAmendments(facilityId),
      ])

      const subLoans = submittedLoans.map(
        (a: {
          loan: any
          amendmentType: string
          amendmentDate: string
          amendmentLenders: any[]
        }) => ({
          ...a.loan,
          amendmentType: a.amendmentType,
          amendmentDate: a.amendmentDate,
          amendmentLenders: a.amendmentLenders,
          status: 'Submitted',
        }),
      )
      const allLoans = draftLoans
        .map((loan: any) => ({ ...loan, status: 'Draft' }))
        .concat(subLoans)

      if (facilityId) {
        return dispatch(getFacilityLoanAmendments(allLoans))
      } else {
        return dispatch(getLoanAmendments(allLoans))
      }
    } catch (error) {
      console.log('THUNK ERROR', error)
    } finally {
      if (facilityId) {
        dispatch(stopLoadingFacilityLoanAmendments())
      }
    }
  },
)

export const loadOneLoanAmendment = coalesceThunkByKey(
  (loanId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = status.toLowerCase()
    return client
      .get(
        `${apiServerUrl}/api/amendment-${statusLower}-loans/one?id=${loanId}`,
      )
      .then(loans => {
        if (statusLower === 'submitted') {
          return dispatch(
            getLoanAmendment({
              ...loans.data.loan,
              amendmentType: loans.data.amendmentType,
              amendmentDate: loans.data.amendmentDate,
              amendmentLenders: loans.data.amendmentLenders,
              status: status,
            }),
          )
        }
        return dispatch(getLoanAmendment({ ...loans.data, status: status }))
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const submitForApprovalAmendment =
  (loan: LoanParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/amendment-draft-loans/submit`, { id: loan.id })
      .then(response => {
        dispatch(submitLoanAmendment(response.data))
        return { success: true }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error || e.message }
      })
  }

export const rejectSubmittedLoanAmendment =
  (loan: LoanParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/amendment-submitted-loans/reject`, {
        id: loan.id,
      })
      .then(response => {
        dispatch(rejectLoanAmendment(response.data))
        return { success: true }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveSubmittedLoanAmendment =
  (loan: LoanParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/amendment-submitted-loans/approve`, {
        id: loan.id,
      })
      .then(async response => {
        // Remove the amendment from state
        dispatch(approveLoanAmendment(response.data))

        // Reload the main loans data to get the updated loan with new terms
        if (loan.facilityId?.id) {
          ;(dispatch as any)(loadAllLoans(loan.facilityId.id))
          ;(dispatch as any)(loadLockedLoans(loan.facilityId.id))
        }

        return { success: true }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteDraftLoanAmendment =
  (ids: string[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/amendment-draft-loans?ids=${ids}`)
      .then(response => {
        dispatch(deleteLoanAmendment(response.data))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
        return {
          success: false,
          payload: e.response?.data?.error || 'Deletion failed',
        }
      })
  }
